import React from "react";
import { SummaryType } from "@ls/grouping-service";
import { titleCase } from "@ls/js-utils";
import { useDispatch, useSelector } from "react-redux";
import { DELETE_DEFINITION } from "./actions";
import {
  selectDefinitions,
  selectFetchingAnalyticsData,
  selectFetchingAnalyticsSbsData,
} from "./selectors";
import { selectSideBySideValue } from "state/selections/selectors";
import CalcType from "state/Constants/CalcType";
import ScaleType from "state/Constants/ScaleType";
import SelectionState from "state/selections/SelectionState";

export const useColumnDefs = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const fetchingAbsData = useSelector(
    selectFetchingAnalyticsData(SummaryType.ABSOLUTE)
  );
  const fetchingRelData = useSelector(
    selectFetchingAnalyticsData(SummaryType.RELATIVE)
  );
  const fetchingAbsSbsData = useSelector(
    selectFetchingAnalyticsSbsData(SummaryType.ABSOLUTE)
  );
  const fetchingRelSbsData = useSelector(
    selectFetchingAnalyticsSbsData(SummaryType.RELATIVE)
  );

  const sideBySideValue = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED)
  );
  const dispatch = useDispatch();
  return React.useMemo(() => {
    return definitions.reduce((columnDefs, analyticDefinition) => {
      const { analytic, scaleType, calcType, key } = analyticDefinition;
      const prefix =
        scaleType === ScaleType.Contribution
          ? "C-"
          : String(scaleType) === "25" || scaleType === ScaleType[25]
          ? "P25-"
          : String(scaleType) === "50" || scaleType === ScaleType[50]
          ? "P50-"
          : String(scaleType) === "75" || scaleType === ScaleType[75]
          ? "P75-"
          : scaleType === ScaleType.Ratio
          ? "Ratio-"
          : "";
      const columnDef = {
        field: key,
        colId: key,
        X_onDelete: () =>
          dispatch({ type: DELETE_DEFINITION, payload: analyticDefinition }),
      };
      switch (calcType) {
        case CalcType.Account:
          columnDef.headerName = `Acct ${prefix}${titleCase(analytic)}`;
          columnDef.loading = fetchingAbsData;
          break;
        case CalcType.Relative:
          columnDef.headerName = `Rel ${prefix}${titleCase(analytic)}`;
          columnDef.loading = fetchingRelData;
          break;
        case CalcType.Benchmark:
          columnDef.headerName = `Bench ${prefix}${titleCase(analytic)}`;
          columnDef.loading = fetchingAbsData;
          break;
        default:
          throw Error("Unknown calc type: " + calcType);
      }
      columnDefs.push(columnDef);
      if (sideBySideValue) {
        const sbsColDef = {
          ...columnDef,
          headerName: columnDef.headerName + " - scenario",
          colId: columnDef.colId + " - scenario",
          field: columnDef.field + " - scenario",
          X_sbs: true,
          X_originalColId: key,
          X_backgroundColor: "#fffb95",
        };
        switch (calcType) {
          case CalcType.Account:
            sbsColDef.loading = fetchingAbsSbsData;
            break;
          case CalcType.Relative:
            sbsColDef.loading = fetchingRelSbsData;
            break;
          case CalcType.Benchmark:
            sbsColDef.loading = fetchingAbsSbsData;
            break;
          default:
            throw Error("Unknown calc type: " + calcType);
        }

        columnDefs.push(sbsColDef);
      }
      return columnDefs;
    }, []);
  }, [
    dispatch,
    definitions,
    fetchingAbsData,
    fetchingRelData,
    sideBySideValue,
    fetchingAbsSbsData,
    fetchingRelSbsData,
  ]);
};
